import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { CheckCircle2, Briefcase, Star, Infinity as InfinityIcon } from 'lucide-react';
import { US_STATES, SEMESTERS, DEGREE_LEVELS, CONCENTRATIONS, FORMATS } from './constants';

const PostInternship = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState('single');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', org_name: '', degree_level: 'Both', concentration: 'Generalist', location: '',
    state: 'Texas', format: 'in_person', semester: 'Fall', weekly_hours: '', population_served: '',
    start_date: '', application_deadline: '', supervision_included: true, description: '', contact_email: '',
  });

  useEffect(() => {
    document.title = 'Post an Internship | I Need A Social Worker';
    axios.get(`${API_URL}/marketplace/internship-plans`).then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in or create an account to post an internship.');
      navigate('/register?type=employer');
      return;
    }
    if (!form.title || !form.org_name || !form.location || !form.description) {
      toast.error('Please complete title, organization, location and description.');
      return;
    }
    setSaving(true);
    try {
      const created = await axios.post(`${API_URL}/internships`, form);
      const internshipId = created.data.internship.internship_id;
      if (selectedPlan === 'unlimited') {
        const res = await axios.post(`${API_URL}/marketplace/internship/checkout/stripe`, {
          plan_id: 'unlimited', origin_url: window.location.origin,
        });
        window.location.href = res.data.url;
        return;
      }
      const res = await axios.post(`${API_URL}/marketplace/internship/checkout/stripe`, {
        plan_id: selectedPlan, origin_url: window.location.origin, internship_id: internshipId,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Badge className="bg-secondary/15 text-secondary mb-3">Internships & Field Placements</Badge>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-primary">Post an Internship</h1>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">Reach motivated BSW & MSW students and build future social workers. Affordable posting options.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {['single', 'featured', 'unlimited'].map((id) => {
            const p = plans[id];
            if (!p) return null;
            const active = selectedPlan === id;
            return (
              <button key={id} type="button" onClick={() => setSelectedPlan(id)} data-testid={`plan-${id}`}
                className={`text-left rounded-2xl border-2 p-6 transition-all ${active ? 'border-secondary shadow-lg bg-white' : 'border-gray-200 bg-white hover:border-secondary/40'}`}>
                {id === 'featured' && <Badge className="bg-secondary text-white mb-2 flex items-center gap-1 w-fit"><Star className="w-3 h-3" /> Featured</Badge>}
                {id === 'unlimited' && <Badge className="bg-primary text-white mb-2 flex items-center gap-1 w-fit"><InfinityIcon className="w-3 h-3" /> Best Value</Badge>}
                <h3 className="font-serif text-lg font-bold text-primary">{p.name}</h3>
                <p className="text-3xl font-bold text-primary mt-2">${p.price}<span className="text-sm text-gray-500 font-normal">{p.billing === 'annual' ? '/year' : ''}</span></p>
                <ul className="mt-4 space-y-2 text-sm text-gray-600">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> {f}</li>)}
                </ul>
                <div className={`mt-4 text-center py-2 rounded-lg text-sm font-semibold ${active ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600'}`}>{active ? 'Selected' : 'Select'}</div>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border p-6 sm:p-8">
          <h2 className="font-serif text-xl font-bold text-primary mb-6 flex items-center gap-2"><Briefcase className="w-5 h-5 text-secondary" /> Internship Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Position title *"><Input value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="int-title" /></Field>
            <Field label="Organization *"><Input value={form.org_name} onChange={(e) => set('org_name', e.target.value)} data-testid="int-org" /></Field>
            <Field label="Degree level"><SelectBox value={form.degree_level} onChange={(v) => set('degree_level', v)} options={DEGREE_LEVELS} testid="int-degree" /></Field>
            <Field label="Concentration"><SelectBox value={form.concentration} onChange={(v) => set('concentration', v)} options={CONCENTRATIONS} testid="int-concentration" /></Field>
            <Field label="Location (City, ST) *"><Input value={form.location} onChange={(e) => set('location', e.target.value)} data-testid="int-location" /></Field>
            <Field label="State"><SelectBox value={form.state} onChange={(v) => set('state', v)} options={US_STATES} testid="int-state" /></Field>
            <Field label="Format"><SelectBox value={form.format} onChange={(v) => set('format', v)} options={FORMATS} testid="int-format" /></Field>
            <Field label="Semester"><SelectBox value={form.semester} onChange={(v) => set('semester', v)} options={SEMESTERS} testid="int-semester" /></Field>
            <Field label="Weekly hours"><Input placeholder="e.g. 16-20 hrs/week" value={form.weekly_hours} onChange={(e) => set('weekly_hours', e.target.value)} data-testid="int-hours" /></Field>
            <Field label="Population served"><Input value={form.population_served} onChange={(e) => set('population_served', e.target.value)} data-testid="int-population" /></Field>
            <Field label="Start date"><Input placeholder="e.g. Aug 26, 2026" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="int-start" /></Field>
            <Field label="Application deadline"><Input value={form.application_deadline} onChange={(e) => set('application_deadline', e.target.value)} data-testid="int-deadline" /></Field>
          </div>
          <Field label="Description * (mention if paid or unpaid here)" className="mt-4">
            <Textarea rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe the placement, responsibilities, whether it is paid or unpaid, and requirements…" data-testid="int-description" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-4">
            <Checkbox checked={form.supervision_included} onCheckedChange={(v) => set('supervision_included', !!v)} data-testid="int-supervision" />
            Supervision included
          </label>

          <Button className="w-full mt-6 bg-secondary hover:bg-secondary-hover text-white h-11" onClick={handleSubmit} disabled={saving} data-testid="submit-internship-btn">
            {saving ? 'Redirecting to payment…' : `Post Internship (${plans[selectedPlan] ? '$' + plans[selectedPlan].price : ''})`}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
    {children}
  </div>
);

const SelectBox = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const lab = typeof o === 'string' ? o : o.label;
        return <SelectItem key={val} value={val}>{lab}</SelectItem>;
      })}
    </SelectContent>
  </Select>
);

export default PostInternship;
